import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import moment from 'moment';
import React, {SyntheticEvent, useCallback, useRef, useState} from 'react';
import DatePicker, {ReactDatePickerProps} from 'react-datepicker';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';
import {FONT_FAMILY_PROXIMA, LEGACY_FONT_PROXIMA_12} from 'styles/FontConstants';
import {ZIndexRole} from 'styles/StyleConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import TextInput, {TextInputProps} from 'ui-library/inputs/TextInput';
import {USER_EDIT_TIME_FORMAT} from 'util/DateConstants';
import {formatDatetimeValue, isValidDateOrDatetimeOrTime, isValidDatetime, isValidTime} from 'util/DateUtil';
import {omit} from 'util/omitKey';
import {BasicEvent, ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

import {DatetimePickerRef, Size, TextInputType} from './InputConstants';

interface DatetimePickerContextInterface {
  containerNodeSelector?: string;
}

const DEFAULT_POPPER_CONTAINER_SELECTOR = 'body';

export const DatetimePickerContext = makeReactContext<DatetimePickerContextInterface>(
  () => ({containerNodeSelector: DEFAULT_POPPER_CONTAINER_SELECTOR}),
  'DatetimePickerContext'
);

const NORMALIZED_TIME_FORMAT = 'H:m';

// 'a', 'p', 'A', 'P' are considered valid meridiems by moment.js but they need to be excluded to prevent an
// auto-complete behavior of '[time] [abbreviated meridiem]' always autocompleting to
// `[time] [complete meridiem]`, thus resetting input cursor to the end. E.g. '12:00 p' -> '12:00 PM'
// autocompletion needs to be prevented
const INVALID_MERIDIEMS = new Set(['a', 'p', 'A', 'P']);

export function getDateObjFromTextValue(value: string, validFormats: Array<string>): Date | null {
  const trimmedValue = value.trim();
  if (!isValidDateOrDatetimeOrTime({textValue: trimmedValue, validFormats})) {
    return null;
  }
  return trimmedValue ? moment(trimmedValue, validFormats, true).toDate() : null;
}

export function getTimeTextValueFromDatetime(
  value: string,
  validDatetimeInputFormats: Array<string>,
  timePresentationalFormat?: string
): string {
  const date = getDateObjFromTextValue(value, validDatetimeInputFormats);
  return date && timePresentationalFormat
    ? formatDatetimeValue(
        `${date.getHours()}:${date.getMinutes()}`,
        [NORMALIZED_TIME_FORMAT],
        timePresentationalFormat
      )
    : '';
}

function isValidFormalTime(value: string, timeFormalInputFormats?: Array<string>): boolean {
  return (
    isValidTime({textValue: value, validTimeFormats: timeFormalInputFormats}) &&
    !INVALID_MERIDIEMS.has(value[value.length - 1])
  );
}

interface DatePickerTimeInputProps extends Omit<TextInputProps, 'onChange'> {
  // We define a custom onChange callback 'onTimeChange' here to bypass react-datepicker's 'onChange' change
  // handler, which among other undesired behavior resets the seconds to zero.
  onTimeChange: (value: string) => void;
  disabled: boolean;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  timeValue: string;
  timeInformalInputFormats: Array<string>;
  timeFormalInputFormats: Array<string>;
  timePresentationalFormat: string;
}

export class DatePickerTimeInput extends React.Component<DatePickerTimeInputProps> {
  static defaultProps = {
    autoFocus: false,
    hasButton: false,
    hasError: false,
    type: TextInputType.TEXT,
  };

  onChange = (e: InputChangeEvent): void => {
    this.props.onTimeChange(e.target.value);
  };

  // Format value to formal format, thus simulating an auto-completion
  handleBlur = (e: InputChangeEvent): void => {
    // If time value is '', autocomplete it to '00:00' (i.e. '12:00 AM')
    const trimmedValue =
      e.target.value ||
      formatDatetimeValue('12:00 AM', [USER_EDIT_TIME_FORMAT], this.props.timePresentationalFormat);
    if (isValidTime({textValue: trimmedValue, validTimeFormats: this.props.timeInformalInputFormats})) {
      this.props.onTimeChange(
        formatDatetimeValue(
          trimmedValue,
          this.props.timeInformalInputFormats,
          this.props.timePresentationalFormat
        )
      );
    }
  };

  render(): JSX.Element {
    return (
      <TextInput
        disabled={this.props.disabled}
        onBlur={this.handleBlur}
        autoFocus={this.props.autoFocus}
        className={classNames(this.props.className, css(styles.timeInput))}
        hasButton={this.props.hasButton}
        hasError={
          !isValidTime({
            textValue: this.props.timeValue.trim(),
            validTimeFormats: this.props.timeInformalInputFormats,
          })
        }
        value={this.props.timeValue}
        onChange={this.onChange}
        onClick={this.props.onClick}
        onKeyDown={this.props.onKeyDown}
        placeholder={this.props.placeholder}
        size={this.props.size}
        type={TextInputType.TEXT}
      />
    );
  }
}

/**
 * A custom time input field, which also includes a 'Close' button and time zone indicator, to pass as
 * `customTimeInput` to `DatePicker`.
 *
 * We render the time zone and the 'Close' button within `customTimeInput` because that is where it visually
 * fits, and where `DatePicker` allows injecting custom components.
 *
 * We wrap the input field and button together as a single helper component because `DatePicker` makes
 * assumptions about the props of `customTimeInput`.
 *
 * @package
 */
export function CustomTimeInputWithCloseButton(
  props: DatePickerTimeInputProps & {closeCalendar: () => void; tzString: string}
): JSX.Element {
  return (
    <div className={css(styles.timeInputSection)}>
      <div className={css(styles.timeInputAndCloseButton)}>
        <DatePickerTimeInput {...props} />
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={props.closeCalendar}
          text="Close"
        />
      </div>
      <span className={css(styles.timeInputTimeZone)}>{props.tzString}</span>
    </div>
  );
}

interface DatePickerDatetimeInputProps extends Omit<TextInputProps, 'onChange'> {
  datetimeInputClassName?: string;
  onChange: (e: InputChangeEvent) => void;
  onFocus: (e: BasicEvent) => void;
  // We need to bypass react-datepicker's 'onChange' because it doesnt accept invalid datetime values or same
  // datetime values, so a custom change handler 'OnDirectChange' is defined here.
  onDirectChange?: (value: string) => void;
  validDatetimeInputFormats: Array<string>;
  datetimePresentationalFormat: string;
  min?: string;
  max?: string;
  datetimeValue: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export class DatePickerDatetimeInput extends React.Component<DatePickerDatetimeInputProps> {
  static defaultProps = {
    autoFocus: false,
    hasButton: false,
    hasError: false,
    type: TextInputType.TEXT,
  };

  // TODO(BNCH-19468): the necessity for shouldComponentUpdate is likely caused by some async updates in
  //  SchemaFieldFilterForm that leads to diverging states. Therefore, identify the root cause and replace
  //  this stop-gap measure
  // Since our custom datetimeValue prop is used to track datetime value, prevent the value prop passed by the
  // library from triggering re-render
  shouldComponentUpdate(nextProps: Readonly<DatePickerDatetimeInputProps>): boolean {
    return !isEqual(omit(this.props, 'value'), omit(nextProps, 'value'));
  }

  formatDatetime(value: string): string {
    return isValidDatetime({
      textValue: value.trim(),
      validDatetimeFormats: this.props.validDatetimeInputFormats,
    })
      ? formatDatetimeValue(
          value.trim(),
          this.props.validDatetimeInputFormats,
          this.props.datetimePresentationalFormat
        )
      : value; // Can't trim on invalid values because it will reset input.
  }

  onChange = (e: InputChangeEvent): void => {
    if (
      isValidDatetime({
        textValue: e.target.value.trim(),
        min: this.props.min,
        max: this.props.max,
        validDatetimeFormats: this.props.validDatetimeInputFormats,
      })
    ) {
      // We need to call this because the current month in the calendar is determined by an uncontrolled state,
      // and only calling this callback can update that state
      this.props.onChange(e);
    }
    if (this.props.onDirectChange) {
      this.props.onDirectChange(e.target.value);
    }
  };

  // Format value to formal format, thus simulating an auto-completion
  handleBlur = (e: InputChangeEvent): void => {
    const trimmedValue = e.target.value.trim();
    if (
      isValidDatetime({
        textValue: trimmedValue,
        validDatetimeFormats: this.props.validDatetimeInputFormats,
      }) &&
      this.props.onDirectChange
    ) {
      this.props.onDirectChange(this.formatDatetime(trimmedValue));
    }
  };

  render(): JSX.Element {
    return (
      <TextInput
        onBlur={this.handleBlur}
        autoFocus={this.props.autoFocus}
        className={classNames(this.props.className, this.props.datetimeInputClassName)}
        inputRef={this.props.inputRef}
        hasButton={this.props.hasButton}
        hasError={
          !isValidDatetime({
            textValue: this.props.datetimeValue.trim(),
            min: this.props.min,
            max: this.props.max,
            validDatetimeFormats: this.props.validDatetimeInputFormats,
          })
        }
        value={this.props.datetimeValue}
        onChange={this.onChange}
        onFocus={this.props.onFocus}
        onClick={this.props.onClick}
        onKeyDown={this.props.onKeyDown}
        placeholder={this.props.placeholder}
        size={this.props.size}
        type={TextInputType.TEXT}
        disabled={this.props.disabled}
      />
    );
  }
}

// This hook handles draft time values
function useTimeState(
  datetimeValue: string,
  validDatetimeInputFormats: Array<string>,
  timePresentationalFormat?: string
): Readonly<[string, React.Dispatch<React.SetStateAction<string>>]> {
  const baseTimeValue = getTimeTextValueFromDatetime(
    datetimeValue,
    validDatetimeInputFormats,
    timePresentationalFormat
  );
  const normalizedBaseTimeValue = getTimeTextValueFromDatetime(
    datetimeValue,
    validDatetimeInputFormats,
    NORMALIZED_TIME_FORMAT
  );
  // This state is primarily used to store invalid time values
  const [draftTimeValue, setDraftTimeValue] = useState(baseTimeValue);
  const oldDatetimeValue = usePrevious(datetimeValue);

  if (
    oldDatetimeValue === datetimeValue ||
    baseTimeValue === draftTimeValue ||
    // Because we consider an empty input to be a valid input and representing "0:0" (i.e."12:00 AM"), we want to avoid
    // autocompleting it with "0:0" because the user might still be editing
    (normalizedBaseTimeValue === '0:0' && draftTimeValue === '')
  ) {
    return [draftTimeValue, setDraftTimeValue];
  } else {
    setDraftTimeValue(baseTimeValue);
    return [baseTimeValue, setDraftTimeValue];
  }
}

interface DatetimePickerBaseProps
  extends Omit<
    ReactDatePickerProps,
    | 'onChange'
    | 'value'
    | 'onCalendarOpen'
    | 'popperContainer'
    | 'onCalendarClose'
    | 'customInput'
    | 'customTimeInput'
    | 'placeholderText'
    | 'dateFormat'
    | 'selected'
    | 'minDate'
    | 'maxDate'
  > {
  datetimeInputClassName?: string;
  size?: Size;
  onChange?: (value: string) => void;
  datePickerPopperContainerSelector?: string;
  placeholder?: string;
  validDatetimeInputFormats: Array<string>;
  datetimePresentationalFormat: string;
  value: string;
  min?: string;
  max?: string;
  datetimePickerRef?: DatetimePickerRef;
  preventDefault?: boolean;
}

type DatetimePickerTimeProps =
  | {
      showTimeInput: true;
      timeInputPlaceholder: string;
      timeInformalInputFormats: Array<string>;
      timeFormalInputFormats: Array<string>;
      timePresentationalFormat: string;
    }
  | {
      showTimeInput: false;
      timeInputPlaceholder?: never;
      timeInformalInputFormats?: never;
      timeFormalInputFormats?: never;
      timePresentationalFormat?: never;
    };

export type DatetimePickerProps = DatetimePickerBaseProps & DatetimePickerTimeProps;

/**
 * Returns string like "UTC-07:00 PDT (America/Los_Angeles)" based on browser timezone and provided datetime.
 *
 * The basic timezone is provided by the browser. The provided datetime is used to determine whether daylight
 * saving is in effect, which affects the UTC offset. If no datetime is provided, uses the current datetime.
 */
function makeTimezoneString(datetime: Date | null): string {
  const dt = datetime ?? new Date();
  const guessedTimezone = moment.tz.guess();
  const tzOffset = moment.tz(dt, guessedTimezone).format('Z');
  const tzAbbr = moment.tz(dt, guessedTimezone).format('z');
  return `UTC${tzOffset} ${tzAbbr}`;
}

/**
 * Component that wraps react-datepicker library's <DatePicker> component with an interface optimized for
 * Benchling's requirements.
 *
 * Main differences:
 *   - Native component's main input box doesn't support invalid values, so we supply a custom input component
 *     DatePickerDatetimeInput and data flow to handle this
 *   - Native component's time input box only supports one format and also doesn't support invalid values, so
 *     we supply a custom time input DatePickerTimeInput and data flow to handle this
 *   - Native interface's popperContainer requires a callback function, but we abstract this away and only
 *     ask the caller to define a node selector
 *   - Native component is closed by clicking outside the component; when nested in floating UI, it is
 *     confusing to know on which level of floating UI to click. This adds a 'Close' button to time inputs.
 *   - Some autocompletion/prettify feature additions:
 *     - When you delete the time value in the time input box or the time part of a datetime value in the main
 *       input box, it reset time to 00:00:00 (i.e. 12:00:00 AM)
 *     - Distinction between input and presentational formats allows us to automatically prettifying valid
 *       values like `2020-1-1 1:1:1 a` into `2020-01-01 01:01:01 AM`
 *
 * There are many more use cases that the library natively supports. For a (non-exhaustive) list of props and
 * a sandbox environment to play with them, it is recommended to visit this site:
 * https://reactdatepicker.com/
 *
 * For a (non-exhaustive) list of available props, visit:
 * https://github.com/Hacker0x01/react-datepicker/blob/master/docs/index.md
 *
 * For the full list of props, I recommend directly inspecting the react component's interface
 * 'ReactDatePickerProps'
 */
export default function DatetimePicker(props: DatetimePickerProps): JSX.Element {
  const [timeValue, setTimeValue] = useTimeState(
    props.value,
    props.validDatetimeInputFormats,
    props.timePresentationalFormat
  );
  const [tzString, setTzString] = useState<string>(
    makeTimezoneString(getDateObjFromTextValue(props.value, props.validDatetimeInputFormats))
  );

  const customInputRef = useRef<HTMLInputElement>(null);
  const datetimePickerInternalRef = useRef<DatePicker | null>(null);

  const callbackRef: DatetimePickerRef = (node) => {
    datetimePickerInternalRef.current = node;
    props.datetimePickerRef?.(node);
  };

  const onTimeChange = (timeValue: string): void => {
    if (isValidFormalTime(timeValue.trim(), props.timeFormalInputFormats)) {
      const normalizedTimeValue = props.timeFormalInputFormats
        ? formatDatetimeValue(timeValue.trim(), props.timeFormalInputFormats, NORMALIZED_TIME_FORMAT)
        : '';
      const currentDateObj: Date =
        getDateObjFromTextValue(props.value, props.validDatetimeInputFormats) || new Date();
      if (normalizedTimeValue) {
        const timeParts = normalizedTimeValue.split(':');
        currentDateObj.setHours(parseInt(timeParts[0], 10));
        currentDateObj.setMinutes(parseInt(timeParts[1], 10));
      } else {
        // Reset time to 00:00:00 (i.e. 12:00:00 AM) when the time value is an empty input ''
        currentDateObj.setHours(0);
        currentDateObj.setMinutes(0);
        currentDateObj.setSeconds(0);
      }
      setValue(currentDateObj);
    }
    setTimeValue(timeValue);
  };

  const setValue = (value: Date | null, event?: SyntheticEvent): void => {
    // An event of type "change" is triggered via a text input in DatePickerDatetimeInput. Since we need to
    // preserve the format of the input (and DatePicker's 'onChange' causes conversion loss from text -> Date),
    // do not update via this callback path
    if (props.onChange && (!event || event.type !== 'change')) {
      const formattedTextValue =
        value != null ? moment(value.toISOString()).format(props.datetimePresentationalFormat) : '';

      if (value != null) {
        const tzString = makeTimezoneString(value);
        setTzString(tzString);
      } else {
        setTzString('');
      }

      props.onChange(formattedTextValue);
    }
  };

  // Sets current datetime on picker opening if current value is empty
  const onClick = (): void => {
    if (!props.value && props.onChange) {
      props.onChange(moment().format(props.datetimePresentationalFormat));
    }
  };

  // Use React's portal to render the calendar component of the DatePicker under a separate DOM node. This is
  // often used to avoid overflow issues arising from the DatetimePicker living in a DOM subtree that disables
  // vertical overflow. E.g. tables in the notebook disable vertical overflow to allow for horizontal overflow,
  // so the calendar component needs to be rendered outside the table subtree via React portal
  const popperContainer = useCallback(
    ({children}: {children: Array<React.ReactNode>}): React.ReactNode => {
      const DOMnode = document.querySelector(props.datePickerPopperContainerSelector!);
      assertAndContinue(DOMnode != null, 'provided node not found');
      // fallback to default selector if the DOMnode is null
      return ReactDOM.createPortal(
        children,
        DOMnode ?? document.querySelector(DEFAULT_POPPER_CONTAINER_SELECTOR)!
      );
    },
    [props.datePickerPopperContainerSelector]
  );

  // Used for resetting DatePickerTimeInput's input on calendar close when the current time value is still a
  // draft
  const resetDatePickerTimeInput = (): void => {
    setTimeValue(
      getTimeTextValueFromDatetime(
        props.value,
        props.validDatetimeInputFormats,
        props.timePresentationalFormat
      )
    );
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.code === 'NumpadEnter') {
      // If DatetimePicker is being rendered from the TransitionRunModal component, we want to prevent default behavior when
      // pressing the 'enter' key to avoid starting and completing the procedure run pre-maturely.
      if (props.preventDefault) {
        e.preventDefault();
      }
      if (datetimePickerInternalRef.current != null) {
        datetimePickerInternalRef.current.setOpen(false);
      }
    }

    // call the provided onKeyDown prop if it exists
    if (props.onKeyDown != null) {
      props.onKeyDown(e);
    }
  };

  return (
    <DatePicker
      {...omit(
        props,
        'onChange',
        'value',
        'size',
        'min',
        'max',
        'wrapperClassName',
        'popperClassName',
        'calendarClassName',
        'validDatetimeInputFormats',
        'datetimePresentationalFormat',
        'timeInformalInputFormats',
        'timeFormalInputFormats',
        'timePresentationalFormat',
        'datePickerPopperContainerSelector',
        'placeholder',
        'timeInputPlaceholder',
        'datetimeInputClassName'
      )}
      wrapperClassName={classNames(css(styles.pickerWrapper), props.wrapperClassName)}
      popperClassName={classNames(css(styles.pickerPopper), props.popperClassName)}
      calendarClassName={classNames(css(styles.pickerCalendar), props.calendarClassName)}
      onCalendarOpen={onClick}
      onCalendarClose={resetDatePickerTimeInput}
      onChange={setValue}
      popperContainer={props.datePickerPopperContainerSelector ? popperContainer : undefined}
      selected={getDateObjFromTextValue(props.value, props.validDatetimeInputFormats)}
      minDate={getDateObjFromTextValue(props.min ?? '', props.validDatetimeInputFormats)}
      maxDate={getDateObjFromTextValue(props.max ?? '', props.validDatetimeInputFormats)}
      placeholderText={props.placeholder}
      customInput={
        <DatePickerDatetimeInput
          datetimeInputClassName={props.datetimeInputClassName}
          size={props.size}
          onDirectChange={props.onChange}
          datetimeValue={props.value}
          min={props.min}
          max={props.max}
          validDatetimeInputFormats={props.validDatetimeInputFormats}
          datetimePresentationalFormat={props.datetimePresentationalFormat}
          disabled={props.disabled}
          onChange={noop}
          onFocus={noop}
          inputRef={customInputRef}
        />
      }
      timeInputLabel=""
      customTimeInput={
        props.showTimeInput ? (
          <CustomTimeInputWithCloseButton
            disabled={
              props.disabled ||
              !isValidDatetime({
                textValue: props.value.trim(),
                validDatetimeFormats: props.validDatetimeInputFormats,
              })
            }
            size={props.size}
            onKeyDown={handleOnKeyDown}
            onTimeChange={onTimeChange}
            timeValue={timeValue}
            timeInformalInputFormats={props.timeInformalInputFormats}
            timeFormalInputFormats={props.timeFormalInputFormats}
            timePresentationalFormat={props.timePresentationalFormat}
            placeholder={props.timeInputPlaceholder}
            tzString={tzString}
            closeCalendar={() => {
              customInputRef.current?.focus();
              datetimePickerInternalRef.current?.setOpen(false);
            }}
          />
        ) : undefined
      }
      ref={callbackRef}
    />
  );
}

DatetimePicker.defaultProps = {
  shouldCloseOnSelect: false,
  showPopperArrow: false,
  timeInputPlaceholder: 'hh:mm AM/PM',
  size: Size.SMALL,
};

const POPPER_HEIGHT = 200;
const POPPER_WIDTH = 222.39;
const TIME_INPUT_MARGIN = 10; // `DatePicker` adds 10px margin to the left and right of the time input

const styles = StyleSheet.create({
  pickerWrapper: {
    width: '100%',
  },
  pickerPopper: {
    width: POPPER_WIDTH,
    height: POPPER_HEIGHT,
    marginTop: '0',
    zIndex: ZIndexRole.DATE_PICKER,
  },
  pickerCalendar: {
    width: '100%',
    height: '100%',
    fontFamily: FONT_FAMILY_PROXIMA,
    fontStyle: 'normal',
    borderColor: GRAY_DARK,
    color: GRAY_DARKER,
    ...LEGACY_FONT_PROXIMA_12,
  },
  timeInput: {
    flexGrow: 1,
    marginRight: TIME_INPUT_MARGIN, // Margin between time input and close button matches margin from border
  },
  timeInputSection: {
    display: 'flex',
    flexDirection: 'column',
    width: POPPER_WIDTH - TIME_INPUT_MARGIN * 2 - 2, // Use the full width, minus built-in margin and border
  },
  timeInputAndCloseButton: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  timeInputTimeZone: {marginLeft: 2, marginTop: 3}, // Indent subtlely beneath the time input
});
